import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Loader2, X, FileText, FileDown, Sparkles } from "lucide-react";
import { api, downloadFile, fmtDate, STATUS_STYLES } from "@/lib/api";

const field = "w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2 text-sm text-navy shadow-none focus:border-gold focus:outline-none";
const label = "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/60";

const EMPTY = { title: "", category: "General", version: "1.0", owner: "", purpose: "", scope: "", responsible_roles: "", steps: [], required_records: "", content: "", status: "Draft", effective_date: "", review_date: "", approval_date: "" };

export default function SOPs() {
  const [sops, setSops] = useState([]);
  const [starters, setStarters] = useState([]);
  const [editing, setEditing] = useState(null);
  const [stepsText, setStepsText] = useState("");
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  const load = () => {
    api.get("/api/app/sops").then((r) => setSops(r.data));
    api.get("/api/app/sops/starters").then((r) => setStarters(r.data));
  };
  useEffect(load, []);

  useEffect(() => {
    if (params.get("new")) setEditing({ ...EMPTY });
  }, [params]);

  const openEdit = (s) => {
    setEditing({ ...s });
    setStepsText((s.steps || []).join("\n"));
  };

  const applyStarter = (starter) => {
    setEditing({ ...EMPTY, title: starter.title, category: starter.category,
      purpose: `Standard process for ${starter.title.toLowerCase()} at our business.` });
    setStepsText("");
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...editing, steps: stepsText.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing.sop_id) await api.put(`/api/app/sops/${editing.sop_id}`, payload);
      else await api.post("/api/app/sops", payload);
      setEditing(null);
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="sops-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Standard Operating Procedures</p>
          <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">SOP Library</h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="sop-add-button" className="btn-gold !py-2.5">
          <Plus className="h-4 w-4" /> Create an SOP
        </button>
      </div>

      <div className="rounded-lg border border-gold/40 bg-gold/5 p-6" data-testid="sop-starters">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-navy">
          <Sparkles className="h-5 w-5 text-golddark" /> Prebuilt starters
        </h2>
        <p className="mt-1 text-xs text-ink/50">AI-assisted SOP drafting is coming in a Phase 2 release — for now, start from a template and document your process.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {starters.map((s) => (
            <button key={s.title} onClick={() => applyStarter(s)} data-testid={`sop-starter-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="rounded-full border border-navy/15 bg-white px-4 py-2 text-xs font-semibold text-navy/75 transition-colors hover:border-gold hover:text-navy">
              {s.title}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-navy/10 bg-white" data-testid="sops-table">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead>
            <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
              {["ID", "Title", "Category", "Version", "Owner", "Status", "Review date", "Export", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sops.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-ink/50">No SOPs yet. Start from a prebuilt starter above.</td></tr>
            )}
            {sops.map((s) => (
              <tr key={s.sop_id} className="border-b border-navy/5 hover:bg-paper/60" data-testid={`sop-row-${s.sop_id}`}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{s.sop_id}</td>
                <td className="px-4 py-3 font-medium text-navy">{s.title}</td>
                <td className="px-4 py-3 text-ink/60">{s.category}</td>
                <td className="px-4 py-3 text-ink/60">v{s.version}</td>
                <td className="px-4 py-3 text-ink/60">{s.owner || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[s.status] || "bg-navy/5 text-navy/60 border-navy/10"}`}>{s.status}</span>
                </td>
                <td className="px-4 py-3 text-ink/60">{fmtDate(s.review_date)}</td>
                <td className="px-4 py-3">
                  <span className="flex gap-2">
                    <button onClick={() => downloadFile(`/api/app/sops/${s.sop_id}/export?format=docx`, `${s.sop_id}.docx`)} title="Export DOCX" data-testid={`sop-export-docx-${s.sop_id}`} className="text-golddark hover:text-navy"><FileDown className="h-4 w-4" /></button>
                    <button onClick={() => downloadFile(`/api/app/sops/${s.sop_id}/export?format=pdf`, `${s.sop_id}.pdf`)} title="Export PDF" data-testid={`sop-export-pdf-${s.sop_id}`} className="text-golddark hover:text-navy"><FileText className="h-4 w-4" /></button>
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => openEdit(s)} data-testid={`sop-edit-${s.sop_id}`} className="text-sm font-semibold text-golddark hover:text-navy">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/50" onClick={() => setEditing(null)} data-testid="sop-drawer-backdrop">
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto bg-white p-8" data-testid="sop-drawer">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold text-navy">{editing.sop_id ? `Edit ${editing.sop_id}` : "Create an SOP"}</h2>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close" data-testid="sop-drawer-close"><X className="h-5 w-5 text-navy/50" /></button>
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={label}>SOP title *</label>
                <input required className={field} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="sop-input-title" />
              </div>
              <div>
                <label className={label}>Category</label>
                <input className={field} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="sop-input-category" />
              </div>
              <div>
                <label className={label}>Version</label>
                <input className={field} value={editing.version} onChange={(e) => setEditing({ ...editing, version: e.target.value })} data-testid="sop-input-version" />
              </div>
              <div>
                <label className={label}>Owner</label>
                <input className={field} value={editing.owner} onChange={(e) => setEditing({ ...editing, owner: e.target.value })} data-testid="sop-input-owner" />
              </div>
              <div>
                <label className={label}>Status</label>
                <select className={field} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="sop-input-status">
                  {["Draft", "Needs Review", "Active"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Purpose</label>
                <textarea className={`${field} min-h-16`} value={editing.purpose} onChange={(e) => setEditing({ ...editing, purpose: e.target.value })} data-testid="sop-input-purpose" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Scope</label>
                <textarea className={`${field} min-h-16`} value={editing.scope} onChange={(e) => setEditing({ ...editing, scope: e.target.value })} data-testid="sop-input-scope" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Responsible roles</label>
                <input className={field} value={editing.responsible_roles} onChange={(e) => setEditing({ ...editing, responsible_roles: e.target.value })} data-testid="sop-input-roles" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Step-by-step procedure (one step per line)</label>
                <textarea className={`${field} min-h-28`} value={stepsText} onChange={(e) => setStepsText(e.target.value)} data-testid="sop-input-steps" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Required records</label>
                <input className={field} value={editing.required_records} onChange={(e) => setEditing({ ...editing, required_records: e.target.value })} data-testid="sop-input-records" />
              </div>
              <div>
                <label className={label}>Effective date</label>
                <input type="date" className={field} value={editing.effective_date || ""} onChange={(e) => setEditing({ ...editing, effective_date: e.target.value })} data-testid="sop-input-effective" />
              </div>
              <div>
                <label className={label}>Review date</label>
                <input type="date" className={field} value={editing.review_date || ""} onChange={(e) => setEditing({ ...editing, review_date: e.target.value })} data-testid="sop-input-review" />
              </div>
            </div>

            <p className="mt-4 rounded-sm border border-gold/40 bg-gold/5 p-3 text-xs italic text-navy/70">
              Setting an SOP to Active records your approval. AI-assisted drafting (Phase 2) never self-approves — a human always
              reviews and approves before an SOP becomes active.
            </p>

            <div className="mt-6 flex gap-3">
              <button type="submit" disabled={busy} data-testid="sop-save-button" className="btn-gold flex-1 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                Save SOP
              </button>
              {editing.sop_id && (
                <button type="button" onClick={async () => { await api.delete(`/api/app/sops/${editing.sop_id}`); setEditing(null); load(); }} data-testid="sop-delete-button" className="rounded-sm border border-red-200 px-5 text-sm font-semibold text-red-600 hover:bg-red-50">
                  Delete
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
